import React, { useCallback, useEffect, useState } from 'react';
import { verifyApi } from '../services/api';
import { AnalysisResponse } from '../types';

interface Props {
  jobId: string;
  jobStatus: string;
  onQueued?: () => void;
  compact?: boolean;
}

export const RerunFailedAnalysisButton: React.FC<Props> = ({
  jobId,
  jobStatus,
  onQueued,
  compact = false,
}) => {
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [queuedCount, setQueuedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [writebackStatus, setWritebackStatus] = useState<string | null>(null);
  const [rerunState, setRerunState] = useState<'idle' | 'queued' | 'running' | 'done' | 'blocked' | 'error'>('idle');

  const applyAnalysisStatus = useCallback((data: AnalysisResponse, activeRerunCount = queuedCount) => {
    const counts = data.summary?.counts;
    const failed = counts?.failed ?? data.results.filter(r => r.analysis_status === 'failed').length;
    const pending = (counts?.queued ?? 0) + (counts?.processing ?? 0);

    setFailedCount(failed);
    setPendingCount(pending);
    setWritebackStatus(data.summary?.writeback_status ?? null);

    if (activeRerunCount > 0) {
      if (pending > 0) {
        setRerunState('running');
        setMessage(`${pending} AI rerun row${pending === 1 ? '' : 's'} still queued or processing. No phone calls are being placed.`);
      } else if (failed > 0) {
        setRerunState('blocked');
        setMessage(`${failed} AI row${failed === 1 ? '' : 's'} still failed after rerun. Open the AI Analysis view for the failure reason.`);
      } else {
        setRerunState('done');
        setMessage(`Rerun complete. ${activeRerunCount} failed AI row${activeRerunCount === 1 ? '' : 's'} finished; writeback is ${data.summary?.writeback_status || 'updating'}.`);
      }
    }
  }, [queuedCount]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (jobStatus !== 'completed') return;
      setChecking(true);
      try {
        const data = await verifyApi.getAnalysisStatus(jobId);
        if (alive) applyAnalysisStatus(data, 0);
      } catch {
        if (alive) setFailedCount(0);
      } finally {
        if (alive) setChecking(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [applyAnalysisStatus, jobId, jobStatus]);

  useEffect(() => {
    if (jobStatus !== 'completed' || queuedCount <= 0 || rerunState === 'done' || rerunState === 'blocked' || rerunState === 'error') {
      return;
    }

    let alive = true;
    const poll = async () => {
      try {
        const data = await verifyApi.getAnalysisStatus(jobId);
        if (alive) applyAnalysisStatus(data, queuedCount);
      } catch {
        if (alive) {
          setRerunState('error');
          setMessage('Could not refresh rerun status. Open AI Analysis to check the latest result.');
        }
      }
    };

    poll();
    const interval = window.setInterval(poll, 3000);
    return () => {
      alive = false;
      window.clearInterval(interval);
    };
  }, [applyAnalysisStatus, jobId, jobStatus, queuedCount, rerunState]);

  if (jobStatus !== 'completed' || checking || (failedCount <= 0 && rerunState === 'idle')) return null;

  const info = rerunState === 'done'
    ? `${queuedCount} failed AI row${queuedCount === 1 ? '' : 's'} finished re-analysis. No phone numbers were called again; existing recordings were reused.`
    : `${failedCount} failed AI row${failedCount === 1 ? '' : 's'} will be sent back through AI analysis. No phone numbers will be called again. The system reuses the existing recording/call SID; Gemini is primary and OpenAI is fallback.`;

  const rerun = async () => {
    const ok = window.confirm(
      `Rerun AI analysis for ${failedCount} failed row${failedCount === 1 ? '' : 's'}?\n\n` +
      `This will NOT call the numbers again. It only reprocesses the existing recordings through Gemini first, with OpenAI as fallback.\n\n` +
      `After you confirm, those rows will move back to queued/processing and the page will update as they finish.`
    );
    if (!ok) return;
    setLoading(true);
    setMessage(null);
    try {
      const res = await verifyApi.rerunFailedAnalysis(jobId);
      setQueuedCount(res.queued);
      setPendingCount(res.queued);
      setRerunState(res.queued > 0 ? 'queued' : 'done');
      setMessage(res.queued > 0
        ? `${res.queued} failed AI row${res.queued === 1 ? '' : 's'} queued for re-analysis. No phone calls are being placed.`
        : res.message);
      onQueued?.();
    } catch (e: any) {
      setRerunState('error');
      setMessage(e?.response?.data?.detail || 'Failed to queue AI rerun.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, position: 'relative' }}>
      <button
        onClick={rerun}
        disabled={loading || rerunState === 'queued' || rerunState === 'running'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 1,
          background: loading || rerunState === 'queued' || rerunState === 'running' ? 'var(--surface-muted)' : 'var(--amber)',
          color: loading || rerunState === 'queued' || rerunState === 'running' ? 'var(--muted)' : 'var(--navy)',
          border: '1px solid var(--amber)',
          padding: compact ? '6px 10px' : '8px 14px',
          borderRadius: 6,
          cursor: loading || rerunState === 'queued' || rerunState === 'running' ? 'not-allowed' : 'pointer',
          fontSize: compact ? 12 : 13,
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {loading ? 'Queueing...' :
          rerunState === 'queued' ? `Queued (${pendingCount})` :
          rerunState === 'running' ? `Running (${pendingCount})` :
          rerunState === 'done' ? 'AI Rerun Done' :
          rerunState === 'blocked' ? `Still Failed (${failedCount})` :
          `Rerun AI (${failedCount})`}
      </button>
      <span
        aria-label={info}
        title={info}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: '1px solid var(--border)',
          color: 'var(--text-strong)',
          background: 'var(--surface-subtle)',
          fontSize: 12,
          fontWeight: 800,
          cursor: 'help',
        }}
      >
        i
      </span>
      {message && (
        <span style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          zIndex: 20,
          width: 260,
          padding: '8px 10px',
          borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontSize: 11,
          lineHeight: 1.4,
          boxShadow: '0 8px 18px rgba(0,0,0,0.25)',
        }}>
          {message}
          {writebackStatus && rerunState === 'done' ? ` Sheet writeback: ${writebackStatus}.` : ''}
        </span>
      )}
    </span>
  );
};
